import { useTemplateStore } from '@/store/template.store';
import { updateLineHeight, updateCharSpacing } from '@/commons/fabricjs/FabricTextHelper';
import { dataStore } from '@/commons/DataStore';
import { useConfigStore } from '@/store/config.store';
import { computed, type Ref, isRef, toRef } from 'vue';
import { storeToRefs } from 'pinia';
import type { ITextField } from '@/typescript/interfaces/ITextField';
import type { ITextboxCustom } from '@/typescript/interfaces/ITextboxCustom';

type SpacingType = 'characterSpacing' | 'lineSpacing';

export function useSpacingControl(
    fieldProp: (() => ITextField) | Ref<ITextField>,
    spacingTypeProp: (() => SpacingType) | Ref<SpacingType> | SpacingType,
) {
    const { currentSide } = storeToRefs(useConfigStore());
    const field = isRef(fieldProp) ? fieldProp : toRef(fieldProp);
    const spacingType = isRef(spacingTypeProp) ? spacingTypeProp : toRef(spacingTypeProp);

    const templateStore = useTemplateStore();

    const spacingValue = computed({
        get() {
            return field.value[spacingType.value] ?? 0;
        },
        set(newSpacing: number) {
            const fabricWorld = dataStore.getFabricWorld(currentSide.value);
            const fieldToEdit = { ...field.value, [spacingType.value]: newSpacing };
            templateStore.updateField(fieldToEdit);

            const fText = fabricWorld.getFabricElementById(field.value.id);
            if (fText && ['text', 'textbox'].includes(fText.type)) {
                if (spacingType.value === 'lineSpacing') {
                    updateLineHeight(fabricWorld.fabricCanvas, fText as ITextboxCustom, newSpacing);
                } else if (spacingType.value === 'characterSpacing') {
                    updateCharSpacing(fabricWorld.fabricCanvas, fText as ITextboxCustom, newSpacing);
                }
            }
        },
    });

    return {
        spacingValue,
    };
}
